import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  getConnectionBetween,
  sendConnectionRequest,
  withdrawConnection,
  acceptConnection,
} from '@/lib/api/connections';
import { useAuth } from '@/lib/hooks/useAuth';

type Props = { targetProfileId: string };

export function ConnectButton({ targetProfileId }: Props) {
  const { profile } = useAuth();
  const qc = useQueryClient();

  const { data: conn, isLoading } = useQuery({
    queryKey: ['connection', profile?.id, targetProfileId],
    queryFn: () => getConnectionBetween(profile!.id, targetProfileId),
    enabled: !!profile && profile.id !== targetProfileId,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      if (!conn) return sendConnectionRequest(profile!.id, targetProfileId);
      if (conn.status === 'pending' && conn.requester_id === profile!.id)
        return withdrawConnection(conn.id);
      if (conn.status === 'pending' && conn.addressee_id === profile!.id)
        return acceptConnection(conn.id);
      if (conn.status === 'accepted') return withdrawConnection(conn.id);
      if (conn.status === 'declined') return sendConnectionRequest(profile!.id, targetProfileId);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connection', profile?.id, targetProfileId] }),
  });

  if (!profile || profile.id === targetProfileId) return null;
  if (isLoading) return <Button variant="outline" size="sm" disabled>…</Button>;

  let label = 'Connect';
  let variant: 'default' | 'outline' | 'destructive' = 'default';

  if (conn?.status === 'pending' && conn.requester_id === profile.id) {
    label = 'Withdraw';
    variant = 'outline';
  } else if (conn?.status === 'pending' && conn.addressee_id === profile.id) {
    label = 'Accept';
  } else if (conn?.status === 'accepted') {
    label = 'Connected';
    variant = 'outline';
  } else if (conn?.status === 'declined') {
    label = 'Connect';
  }

  return (
    <Button
      size="sm"
      variant={variant}
      disabled={isPending}
      onClick={() => mutate()}
      className={
        variant === 'default'
          ? 'bg-navy text-cream hover:bg-navy-deep'
          : undefined
      }
    >
      {isPending ? '…' : label}
    </Button>
  );
}
